import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { ArrowLeft, History, MessageSquare, Building2, Package, FileText, Printer, StickyNote } from "lucide-react";
import MessageComposer, { MessageAttachments } from "@/components/MessageComposer";

const STATUSES = ["Orders In", "Picking", "To Invoice", "Completed"];
const STATUS_COLOR = {
  "Orders In": "brand", "Picking": "warning", "To Invoice": "warning",
  "Completed": "success",
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = ["admin", "user", "admin_reseller"].includes(user.role);

  const [order, setOrder] = useState(null);
  const [company, setCompany] = useState(null);
  const [product, setProduct] = useState(null);
  const [siblingOrders, setSiblingOrders] = useState([]); // other orders that share the same Source_PO_ID
  const [allProducts, setAllProducts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState("");
  const [sending, setSending] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [dockets, setDockets] = useState([]);
  const [generatingDocket, setGeneratingDocket] = useState(false);
  const [docketNoteDraft, setDocketNoteDraft] = useState({});
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [allOrders, inv] = await Promise.all([
        api.get(`/orders`).then((r) => r.data),
        api.get(`/inventory`).then((r) => r.data),
      ]);
      setAllProducts(inv);
      const orderResp = allOrders.find((o) => o.id === id);
      if (!orderResp) { setErr("Order not found"); return; }
      setOrder(orderResp);
      setProduct(inv.find((p) => p.id === orderResp.Product_ID));
      // Sibling orders born from the same Source PO (so we render multi-item POs as ONE order detail)
      const siblings = orderResp.Source_PO_ID
        ? allOrders.filter((o) => o.Source_PO_ID === orderResp.Source_PO_ID)
        : [orderResp];
      setSiblingOrders(siblings);
      const [co, lg] = await Promise.all([
        api.get(`/companies/${orderResp.Company_ID}`),
        api.get(`/orders/${id}/log`),
      ]);
      setCompany(co.data); setLogs(lg.data);
      // get-or-create channel
      const ch = await api.post(`/orders/${id}/channel`);
      setChannel(ch.data);
      const ms = await api.get(`/channels/${ch.data.id}/messages`);
      setMessages(ms.data);
      // dockets linked to this order
      try {
        const dk = await api.get(`/orders/${id}/dockets`);
        setDockets(dk.data || []);
      } catch { setDockets([]); }
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (newStatus) => {
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/orders/${id}`, { Order_Status: newStatus });
      setOrder(data);
      const lg = await api.get(`/orders/${id}/log`);
      setLogs(lg.data);
      toast.success(`Status changed to ${newStatus}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingStatus(false); }
  };

  const sendMessage = async (body, attachments) => {
    if (!channel) return;
    try {
      const { data } = await api.post(`/channels/${channel.id}/messages`, { Body: body, Attachments: attachments });
      setMessages((prev) => [...prev, data]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const generateDocket = async () => {
    setGeneratingDocket(true);
    try {
      const { data } = await api.post(`/orders/${id}/docket`);
      setDockets((prev) => {
        if (prev.find((d) => d.id === data.id)) return prev;
        return [data, ...prev];
      });
      toast.success(`Docket ${data.Docket_Number} ready to print.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGeneratingDocket(false); }
  };

  const saveDocketNote = async (docketId) => {
    const next = docketNoteDraft[docketId];
    if (next === undefined) return;
    try {
      const { data } = await api.patch(`/dockets/${docketId}`, { Internal_Notes: next });
      setDockets((prev) => prev.map((d) => (d.id === data.id ? data : d)));
      setDocketNoteDraft((s) => ({ ...s, [docketId]: undefined }));
      toast.success("Docket note saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (err) return <div className="es-card p-4 text-sm text-red-700">{err}</div>;
  if (!order) return <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Loading order…</div>;

  // Multi-line PO support: if this order has siblings (same Source_PO_ID), show them all
  const lines = siblingOrders.length > 0 ? siblingOrders : [order];
  const totalQty = lines.reduce((s, o) => s + (Number(o.Quantity_Ordered) || 0), 0);
  const productFor = (oid) => allProducts.find((p) => p.id === oid);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="es-btn-ghost h-8 -ml-2" data-testid="order-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* HEADER — big product + chunky Qty badge */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <div className="es-overline text-brand-600 mb-2">// order detail</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="order-detail-product">
            {lines.length > 1 ? `${lines.length} items · ${company?.Company_Name || ""}` : (product?.Product_Name || "—")}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-zinc-600 flex-wrap">
            <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" />{company?.Company_Name}</span>
            {order.Source_PO_ID && (
              <span className="flex items-center gap-1.5 font-mono text-xs">
                <FileText className="w-3.5 h-3.5" /> PO {order.Source_PO_ID.slice(0, 8)}
              </span>
            )}
            <span className="font-mono text-xs text-zinc-400">ID · {order.id.slice(0, 8)}</span>
          </div>
        </div>

        {/* Big Qty pill + status select */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div
            data-testid="order-detail-qty-pill"
            className="inline-flex items-baseline gap-2 rounded-2xl bg-brand-50 ring-1 ring-brand-200 px-5 py-3"
            title={lines.length > 1 ? "Total quantity across all line items" : "Quantity ordered"}
          >
            <Package className="w-5 h-5 text-brand-700 self-center" />
            <span className="font-display text-4xl font-extrabold tracking-tight text-brand-900 leading-none">
              {totalQty.toLocaleString()}
            </span>
            <span className="text-xs uppercase tracking-wider font-bold text-brand-700">units</span>
          </div>
          <div className="flex flex-col gap-1.5 items-end">
            <span className={`es-badge es-badge-${STATUS_COLOR[order.Order_Status] || "neutral"}`}>{order.Order_Status}</span>
            {canEdit && (
              <select
                data-testid="order-status-select"
                value={order.Order_Status}
                onChange={(e) => updateStatus(e.target.value)}
                disabled={savingStatus}
                className="es-select h-9 text-xs"
              >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* LINE ITEMS — chunky list so multi-product POs read clearly */}
      <div className="es-card overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
            <Package className="w-4 h-4 text-brand-600" /> Line Items
            <span className="es-badge es-badge-neutral text-[10px] ml-1">{lines.length}</span>
          </h3>
        </div>
        <ul className="divide-y divide-zinc-100" data-testid="order-detail-lines">
          {lines.map((ln) => {
            const p = productFor(ln.Product_ID);
            const isCurrent = ln.id === order.id;
            return (
              <li
                key={ln.id}
                data-testid={`order-line-${ln.id}`}
                onClick={() => !isCurrent && navigate(`/orders/${ln.id}`)}
                className={`flex items-center gap-4 px-5 py-4 ${!isCurrent ? "cursor-pointer hover:bg-zinc-50" : "bg-brand-50/40"}`}
                title={isCurrent ? "This line" : "Open this line"}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-zinc-900 text-base">{p?.Product_Name || "Unmatched product"}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-0.5">
                    {p?.SKU ? `SKU ${p.SKU}` : "no SKU"}
                    {p?.Colour ? ` · ${p.Colour}` : ""}
                    {p?.Category ? ` · ${p.Category}` : ""}
                    {isCurrent && lines.length > 1 && <span className="ml-2 text-brand-700 font-semibold">· current</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-2xl font-extrabold font-mono leading-none text-zinc-900">
                    × {Number(ln.Quantity_Ordered || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1 font-semibold">qty</div>
                </div>
                <span className={`es-badge es-badge-${STATUS_COLOR[ln.Order_Status] || "neutral"} flex-shrink-0`}>{ln.Order_Status}</span>
              </li>
            );
          })}
        </ul>
        {lines.length > 1 && (
          <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Total</span>
            <span className="font-display text-xl font-extrabold font-mono text-zinc-900">× {totalQty.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* DOCKETS — printable pickup slips linked to this order */}
      {canEdit && (
        <div className="es-card overflow-hidden" data-testid="order-docket-section">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand-600" /> Pickup Dockets
              <span className="es-badge es-badge-neutral text-[10px] ml-1">{dockets.length}</span>
            </h3>
            <button
              onClick={generateDocket}
              disabled={generatingDocket}
              data-testid="order-generate-docket"
              className="es-btn-primary h-9 px-3 disabled:opacity-60"
            >
              <FileText className="w-4 h-4" /> {generatingDocket ? "Creating…" : (dockets.length > 0 ? "Add another docket" : "Generate docket")}
            </button>
          </div>
          {dockets.length === 0 ? (
            <div className="px-5 py-6 text-xs font-mono text-zinc-500 text-center">
              No docket yet. Click <strong>Generate docket</strong> to create a printable pickup slip for this order.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {dockets.map((dk) => {
                const draft = docketNoteDraft[dk.id];
                const noteDirty = draft !== undefined && draft !== (dk.Internal_Notes || "");
                return (
                  <li key={dk.id} data-testid={`order-docket-${dk.id}`} className="px-5 py-4 flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold font-mono text-base text-zinc-900">{dk.Docket_Number}</span>
                        <span className={`es-badge es-badge-${{
                          "Pending": "warning", "Picked Up": "success", "Cancelled": "error",
                        }[dk.Docket_Status] || "neutral"}`}>{dk.Docket_Status}</span>
                        <span className="text-[11px] font-mono text-zinc-500">Pickup by {dk.Pickup_Deadline}</span>
                      </div>
                      <div className="mt-2">
                        <label className="block text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1 inline-flex items-center gap-1">
                          <StickyNote className="w-3 h-3" /> Docket Comments (internal)
                        </label>
                        <textarea
                          data-testid={`docket-note-${dk.id}`}
                          rows={2}
                          value={draft !== undefined ? draft : (dk.Internal_Notes || "")}
                          onChange={(e) => setDocketNoteDraft((s) => ({ ...s, [dk.id]: e.target.value }))}
                          placeholder="e.g. Customer arriving 7am — leave on pallet 3"
                          className="es-input h-auto py-2 resize-none w-full text-sm"
                        />
                        {noteDirty && (
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              onClick={() => saveDocketNote(dk.id)}
                              data-testid={`docket-note-save-${dk.id}`}
                              className="text-xs px-2.5 py-1 rounded-md bg-brand-600 text-white hover:bg-brand-700"
                            >Save note</button>
                            <button
                              onClick={() => setDocketNoteDraft((s) => ({ ...s, [dk.id]: undefined }))}
                              className="text-xs px-2.5 py-1 rounded-md border border-zinc-200"
                            >Cancel</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex sm:flex-col items-stretch gap-2 flex-shrink-0">
                      <button
                        onClick={() => window.open(`/dockets/${dk.id}/print`, "_blank")}
                        data-testid={`docket-print-${dk.id}`}
                        className="inline-flex items-center justify-center gap-2 h-9 px-3 bg-white text-zinc-700 text-sm font-semibold rounded-lg border border-zinc-300 hover:border-brand-500 hover:text-brand-700"
                      >
                        <Printer className="w-4 h-4" /> Print
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status timeline */}
        <div className="es-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-brand-600" />
            <h3 className="font-display font-bold text-sm uppercase tracking-wider">Status Timeline</h3>
          </div>
          <ul className="space-y-4" data-testid="order-status-log">
            {logs.length === 0 && <li className="text-xs font-mono text-zinc-500">No status changes recorded.</li>}
            {logs.map((l) => (
              <li key={l.id} className="border-l-2 border-brand-500 pl-3">
                <div className="text-xs font-mono text-zinc-500">{new Date(l.created_date).toLocaleString()}</div>
                <div className="text-sm font-semibold mt-0.5">
                  {l.From_Status ? `${l.From_Status} → ` : ""}{l.To_Status}
                </div>
                <div className="text-xs text-zinc-600">by {l.Changed_By_Name}</div>
                {l.Notes && <div className="text-xs italic text-zinc-500 mt-1">{l.Notes}</div>}
              </li>
            ))}
          </ul>
        </div>

        {/* Order info */}
        <div className="es-card p-5 lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider">Details</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Detail label="Delivery Address" value={order.Delivery_Address || "—"} />
            <Detail label="Notes" value={order.Notes || "—"} />
            <Detail label="Source PO" value={order.Source_PO_ID ? order.Source_PO_ID.slice(0, 8) : "—"} />
            <Detail label="Manual Review" value={order.Manual_Review_Required ? "Yes" : "No"} />
            <Detail label="Created" value={order.created_date?.slice(0, 16).replace("T", " ")} />
            <Detail label="Updated" value={order.updated_date?.slice(0, 16).replace("T", " ")} />
          </div>
        </div>
      </div>

      {/* Chat / comments */}
      <div className="es-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-4 h-4 text-brand-600" />
          <h3 className="font-display font-bold text-sm uppercase tracking-wider">Conversation</h3>
          <span className="es-badge es-badge-neutral ml-2">{messages.length}</span>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2 mb-4" data-testid="order-conversation">
          {messages.length === 0 && <div className="text-xs font-mono text-zinc-500">No messages yet. Start the conversation below.</div>}
          {messages.map((m) => (
            <div key={m.id} className="flex gap-3" data-testid={`message-${m.id}`}>
              <div className="w-9 h-9 bg-zinc-200 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                {m.Sender_Name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-semibold text-sm">{m.Sender_Name}</span>
                  <span className="es-badge es-badge-neutral text-[10px]">{m.Sender_Role}</span>
                  <span className="text-xs font-mono text-zinc-400">{new Date(m.created_date).toLocaleString()}</span>
                </div>
                {m.Body && <div className="text-sm text-zinc-800 mt-1 whitespace-pre-wrap">{m.Body}</div>}
                <MessageAttachments attachments={m.Attachments} />
              </div>
            </div>
          ))}
        </div>
        <MessageComposer onSend={sendMessage} />
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="es-overline mb-1">{label}</div>
      <div className="text-sm text-zinc-900">{value}</div>
    </div>
  );
}
